import React, { useState, useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';

import { customerSVC, subscriptionsSVC } from '@com/core/common/services/api';

import { TAddressesGET } from '@com/core/common/types/customer_address';
import ToastContext from '@com/core/common/contexts/toastContext';
import { useTranslation } from '@com/core/common/translations/i18n';

import { CartContext } from '@com/core/common/contexts/cartContextDaily';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
import { AppContext } from '@com/core/common/contexts/appContext';
import Analytics from '@com/core/common//utils/analytics';
import AccountLayout from '../common/AccountLayout';
import AddressListItem from './AddressListItem';
import EmptyState from '../common/EmptyState';
import AddressPhoneVerifyModal from '../common/AddressPhoneVerifyModal';

type TAddress = TAddressesGET['data'][0];

type TProps = {
    onEdit: (address: TAddress) => void;
    onAdd: () => void;
};

const AddressList = ({ onAdd, onEdit }: TProps): JSX.Element => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);
    const { addresses: addressesCtx, customer, subscriptions } = useContext(CustomerContext);
    const { getCart } = useContext(CartContext);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [addresses, setAddresses] = useState<TAddress[] | null>([]);
    const [showPhoneVerify, setShowPhoneVerify] = useState(false);
    const [currentAddressId, setAddressId] = useState(0);

    const { showToast } = useContext(ToastContext);

    const getData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await customerSVC(locale.apiLocale).address.get();
            if (data.error) {
                setError(data.error);
                showToast('error', JSON.stringify(data.error), 'error');
            } else {
                setAddresses(data.data);
                setIsLoading(false);
            }
        } catch (err) {
            showToast('error', err.error || t('something-went-wrong'));
            setError(err.error || t('something-went-wrong'));
        }
        // showToast would fetch data each time a message is shown
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getData();
        // only needs to fire when there's a change in customer
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    // Tracking
    useEffect(() => {
        Analytics.trackPage(locale, {
            event: 'page_addresses',
            eventAction: 'addresses',
            eventLabel: '',
        });
    }, []);

    const handleSetDefault = async (addressId: number, validatePhone? = true): Promise<void> => {
        setIsLoading(true);
        setAddressId(addressId);
        try {
            if (subscriptions.isSubscriptionsPage) {
                const { addressCode } = addresses.find(address => address.id === addressId);
                const { data } = await subscriptionsSVC(locale.apiLocale).setSubscriptionPreferences(
                    { addressCode, view: 'product' },
                    subscriptions.isSubscriptionsPage,
                );

                if (data.error) {
                    setError(data.error);
                    showToast('error', JSON.stringify(data.error), 'error');
                } else {
                    subscriptions.set({
                        ...subscriptions.subscriptionsData,
                        preferences: { ...data.data.preferences },
                    });
                    subscriptions.toggleIsSubscriptionsPage(false);
                    addressesCtx.toggleShowAddressModal(false);
                    setIsLoading(false);
                }
            } else {
                const addressToSet = addresses?.find(ad => ad.id === addressId);
                if (!addressToSet?.isPhoneVerified && validatePhone) {
                    setShowPhoneVerify(true);
                    setIsLoading(false);
                } else {
                    const { data } = await customerSVC(locale.apiLocale).address.setDefault(addressId.toString());
                    if (data.error) {
                        setError(data.error);
                        showToast('error', JSON.stringify(data.error), 'error');
                    } else {
                        setShowPhoneVerify(false);
                        setAddresses(data.data);
                        getCart(false);
                        addressesCtx.toggleShowAddressModal(false);
                        setIsLoading(false);
                    }
                }
            }
        } catch (err) {
            showToast('error', err.error || t('something-went-wrong'));
            setError(err.error || t('something-went-wrong'));
        }
    };

    const handleDeleteAddress = async (addressId: string): Promise<void> => {
        setIsLoading(true);
        try {
            const { data } = await customerSVC(locale.apiLocale).address.remove(addressId);
            if (data.error) {
                setError(data.error);
                showToast('error', JSON.stringify(data.error), 'error');
            } else {
                setAddresses(data.data);
                setIsLoading(false);
            }
        } catch (err) {
            showToast('error', err.error || t('something-went-wrong'));
            setError(err.error || t('something-went-wrong'));
        }
    };

    if (!isLoading && (!addresses || !addresses.length)) {
        return <EmptyState type="addresses" ctaClick={onAdd} />;
    }

    let primaryAddress = null;
    let secondaryAddresses = [];

    if (subscriptions.isSubscriptionsPage) {
        primaryAddress =
            addresses &&
            addresses.find(
                address => subscriptions.subscriptionsData.preferences.address.addressCode === address.addressCode,
            );
        secondaryAddresses =
            addresses &&
            addresses.filter(
                address => subscriptions.subscriptionsData.preferences.address.addressCode !== address.addressCode,
            );
    } else {
        primaryAddress = addresses && addresses.find(address => !!address.defaultShipping);
        secondaryAddresses = addresses && addresses.filter(address => !address.defaultShipping);
    }

    return (
        <AccountLayout
            titleKey={t('_address_list_title')}
            section="addresses"
            subtitleKey={t('_address_list_subtitle')}
            ctaTitle={addresses && addresses.length ? t('add-new-address') : ''}
            ctaAction={onAdd}
            isLoading={isLoading}
            error={error}>
            <>
                <AddressListContainer>
                    {primaryAddress && (
                        <section>
                            <h2>{t('current_delivery_address')}</h2>
                            <AddressListItem
                                key={primaryAddress.id}
                                handleEditAddressButton={onEdit}
                                handleDeleteAddress={handleDeleteAddress}
                                address={primaryAddress}
                                isDefault
                            />
                        </section>
                    )}
                    {secondaryAddresses && secondaryAddresses.length > 0 && (
                        <>
                            <section>
                                <h2>{t('_address_other-addresses')}</h2>
                                <p className="warning">{t('changing_your_delivery_address_warning')}</p>
                                {secondaryAddresses.map((address: TAddress) => (
                                    <AddressListItem
                                        key={address.id}
                                        handleEditAddressButton={onEdit}
                                        handleDeleteAddress={handleDeleteAddress}
                                        address={address}
                                        onSelectAddress={handleSetDefault}
                                    />
                                ))}
                            </section>
                        </>
                    )}
                </AddressListContainer>
                {showPhoneVerify && (
                    <AddressPhoneVerifyModal
                        toggle={(): void => {
                            setShowPhoneVerify(show => !show);
                        }}
                        successCallback={(): void => {
                            handleSetDefault(currentAddressId, false);
                        }}
                        address={addresses?.find(ad => ad.id === currentAddressId) || null}
                    />
                )}
            </>
        </AccountLayout>
    );
};

const AddressListContainer = styled.div`
    section {
        border: 1px solid ${({ theme }): string => theme.colors.border};
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    }

    section + section {
        margin-top: 24px;
    }

    h2 {
        padding: 12px 16px;
        font-weight: 600;
        border-bottom: 1px solid ${({ theme }): string => theme.colors.border};
        border-radius: ${({ theme }): string => theme.metrics.borderRadius}
            ${({ theme }): string => theme.metrics.borderRadius} 0 0;
    }

    p.warning {
        background-color: #fffcdc;
        border-radius: ${({ theme: { metrics } }): string => metrics.borderRadius}
            ${({ theme: { metrics } }): string => metrics.borderRadius} 0 0;
        padding: 11px 16px;
        font-size: 0.875rem;
        color: #49450b;
        border-bottom: 1px solid ${({ theme }): string => theme.colors.border};
    }
`;

export default AddressList;
